import React from 'react';
import memoize from 'memoize-one';
import { TabView } from './models/tabView';
import { NavigationPanelComponent } from './components/navigationPanelComponent';
import { TabNavigationItem } from './models/tabNavigationItem';
import { RouteTabView } from './models/routeTabView';
import { withResizeDetector } from '../ResizeDetectorWrapper/withResizeDetector';
import { CustomContextMenuContainer, IContextMenuItem, Icon } from 'tacho-ui';
import { injectIntl } from 'react-intl';
import { IntlShape } from 'react-intl/lib';

interface NavigationPanelContainerProps {
    intl: IntlShape;
    width: number;
    tabs: Array<TabView | RouteTabView<any>>;
    activeTabIndex: number;
    upperCaseTabs: boolean;
    id: string;
    className?: string;
    suppressDropdown?: boolean;
    customContextMenuItems?: Array<IContextMenuItem>;
    showContextMenu?: boolean;
    inModal?: boolean;
    closeTabsHandler?: () => void;
    onTabReset?: (tabIndex: number) => void;
    onTabSelected: (tabIndex: number) => void;
    onTabClose: (tabIndex: number) => void;
}

interface TabsNavigationComponentState {
    hiddenTabsCount: number | undefined;
}

class NavigationPanelContainer extends React.Component<NavigationPanelContainerProps, TabsNavigationComponentState> {
    private visibleTabsRefrences: Array<HTMLLIElement> = [];
    private tabs = memoize(
        (tabs: Array<TabView | RouteTabView<any>>): Array<TabNavigationItem> =>
            tabs.map(
                (t, i): TabNavigationItem => ({
                    index: i,
                    label: t.name,
                    isCloseable: t.canCloseTab,
                    isResetable: t.canResetTab,
                    disabled: t.disabled,
                    icon: t.icon,
                    onDuplicate: t.onDuplicate,
                }),
            ),
    );

    public constructor(props: NavigationPanelContainerProps) {
        super(props);
        this.state = {
            hiddenTabsCount: undefined,
        };
    }

    public componentDidMount(): void {
        this.onComponentRendered(0);
    }

    public componentDidUpdate(prevProps: NavigationPanelContainerProps): void {
        this.onComponentRendered(prevProps.width);
    }

    private onComponentRendered(prevWidth: number): void {
        const hiddenTabsCount = this.props.tabs.length - this.calculateTabsCountToFitScreen(prevWidth);
        if (this.state.hiddenTabsCount === hiddenTabsCount) {
            return;
        }

        this.setState({ hiddenTabsCount });
    }

    private onTabClose(tabIndex: number): void {
        this.props.onTabClose(tabIndex);
        this.setState({
            hiddenTabsCount: Math.max((this.state.hiddenTabsCount || 0) - 1, 0),
        });
    }

    private onTabsClose(tabs: Array<TabNavigationItem>, closeTabsHandler?: () => void): void {
        const closableTabs = tabs.filter(tab => tab.isCloseable);
        closeTabsHandler ? closeTabsHandler() : closableTabs.forEach(tab => this.onTabClose(tab.index));
        this.setState({ hiddenTabsCount: Math.max((this.state.hiddenTabsCount || 0) - closableTabs.length, 0) });
    }

    private calculateTabsCountToFitScreen(prevWidth: number): number {
        if (this.props.suppressDropdown) return this.props.tabs.length;
        const resizeUpThrottlePx = 60; // do not consider resize up belowe 60px change
        if (this.props.width === 0) {
            return this.visibleTabsRefrences.length;
        } else if (this.props.width > prevWidth + resizeUpThrottlePx) {
            return this.props.tabs.length;
        }

        let tabsWidth = 30 * this.visibleTabsRefrences.length;
        let tabIndex = 0;

        for (tabIndex; tabIndex < this.visibleTabsRefrences.length; ++tabIndex) {
            tabsWidth = tabsWidth + this.visibleTabsRefrences[tabIndex].getBoundingClientRect().width;
            if (tabsWidth > this.props.width) {
                break;
            }
        }
        return tabIndex;
    }

    private setTabReference(domReference: HTMLLIElement): void {
        if (domReference) {
            this.visibleTabsRefrences.push(domReference);
        }
    }

    private getContextMenuItems(tabs: Array<TabNavigationItem>) {
        const defaultContextMenuItems = [
            {
                label: this.props.intl.formatMessage({ id: 'dataAnalysisCloseAllTabs' }),
                onItemClick: () => {
                    this.onTabsClose(tabs, this.props.closeTabsHandler);
                },
                iconName: Icon.Clear,
                disabled: !tabs.some(tab => tab.isCloseable),
            },
        ];

        if (this.props.customContextMenuItems) return [...defaultContextMenuItems, ...this.props.customContextMenuItems];
        return defaultContextMenuItems;
    }

    public render(): JSX.Element {
        this.visibleTabsRefrences = [];
        const tabs = this.tabs(this.props.tabs);
        const firstHiddenTabIndex = tabs.length - (this.state.hiddenTabsCount || 0);
        const dropdownTabs = tabs.slice(firstHiddenTabIndex);
        const visibleTabs = tabs.slice(0, firstHiddenTabIndex);

        return (
            <CustomContextMenuContainer
                items={this.getContextMenuItems(tabs)}
                visible={this.props.showContextMenu ? undefined : false}
                className="contextMenu"
            >
                <NavigationPanelComponent
                    activeTabIndex={this.props.activeTabIndex}
                    navigationId={this.props.id}
                    linkNavTabsRef={ref => this.setTabReference(ref)}
                    className={this.props.className}
                    upperCaseTabs={this.props.upperCaseTabs}
                    onTabSelect={tabIndex => this.props.onTabSelected(tabIndex)}
                    onTabClose={tabIndex => this.onTabClose(tabIndex)}
                    visibleTabs={visibleTabs}
                    dropdownTabs={dropdownTabs}
                    inModal={this.props.inModal}
                    onTabReset={tabIndex => this.props.onTabReset && this.props.onTabReset(tabIndex)}
                />
            </CustomContextMenuContainer>
        );
    }
}

export default withResizeDetector(injectIntl(NavigationPanelContainer));
